import React, { Component, ReactNode } from 'react';

import { Box, Typography, Stack, SvgIcon } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';

import { CustomButton } from '../customButton/CustomButton';

import * as Sentry from '@sentry/react';

interface IProps {
  children: ReactNode;
  fallbackTitle?: string;
  fallbackMessage?: string;
  showRefresh?: boolean;
  onRetry?: () => void;
  reportIssue?: () => void;
}

interface IState {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
}

export class VideoErrorBoundary extends Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): IState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('VideoErrorBoundary caught an error:', error, errorInfo);

    // Enhanced logging for better issue identification
    console.group('🎥 VIDEO ERROR BOUNDARY - Detailed Error Report');
    console.error('⚠️ Error Message:', error.message);
    console.error('📍 Error Name:', error.name);
    console.error('🔗 Error Stack:', error.stack);
    console.error('🧩 Component Stack:', errorInfo.componentStack);

    // Log environment and browser info
    console.group('🌐 Environment Information');
    console.log('User Agent:', navigator.userAgent);
    console.log('URL:', window.location.href);
    console.log('Timestamp:', new Date().toISOString());
    console.log('Language:', navigator.language);
    console.log('Online Status:', navigator.onLine);
    console.log('Platform:', navigator.platform);
    console.groupEnd();

    // Log media capabilities
    console.group('🎬 Media Capabilities');
    console.log(
      'MediaDevices API:',
      'mediaDevices' in navigator ? '✅ Supported' : '❌ Not Supported',
    );
    console.log(
      'getUserMedia:',
      'getUserMedia' in (navigator.mediaDevices || {})
        ? '✅ Supported'
        : '❌ Not Supported',
    );
    console.log(
      'WebRTC:',
      'RTCPeerConnection' in window ? '✅ Supported' : '❌ Not Supported',
    );
    console.log(
      'MediaRecorder:',
      'MediaRecorder' in window ? '✅ Supported' : '❌ Not Supported',
    );

    // Check for specific video/audio constraints
    if (
      navigator.mediaDevices &&
      navigator.mediaDevices.getSupportedConstraints
    ) {
      const constraints = navigator.mediaDevices.getSupportedConstraints();
      console.log('Supported Constraints:', constraints);
    }
    console.groupEnd();

    // Log common video error patterns
    console.group('🔍 Error Pattern Analysis');
    if (error.message.includes('Permission')) {
      console.warn('🚫 PERMISSION ISSUE: Camera/microphone access denied');
    }
    if (error.message.includes('NotFound')) {
      console.warn('📹 DEVICE ISSUE: Camera or microphone not found');
    }
    if (error.message.includes('NotAllowed')) {
      console.warn('🔒 ACCESS ISSUE: User denied media access');
    }
    if (error.message.includes('NotReadable')) {
      console.warn(
        '🔧 HARDWARE ISSUE: Device is already in use or hardware error',
      );
    }
    if (error.message.includes('OverConstrained')) {
      console.warn(
        '⚙️ CONSTRAINT ISSUE: Requested constraints cannot be satisfied',
      );
    }
    if (error.message.includes('NetworkError')) {
      console.warn('🌐 NETWORK ISSUE: Network error during video processing');
    }
    if (error.message.includes('SecurityError')) {
      console.warn('🛡️ SECURITY ISSUE: Security error in video access');
    }
    console.groupEnd();

    console.groupEnd(); // End main error group

    this.setState({
      error,
      errorInfo,
    });

    // Simple Sentry logging with essential context
    Sentry.captureException(error, {
      tags: {
        errorBoundary: true,
        context: 'video-recording',
        component: 'VideoErrorBoundary',
      },
      extra: {
        componentStack: errorInfo.componentStack,
        userAgent: navigator.userAgent,
        mediaSupport: {
          mediaDevices: 'mediaDevices' in navigator,
          getUserMedia: 'getUserMedia' in (navigator.mediaDevices || {}),
          webRTC: 'RTCPeerConnection' in window,
          mediaRecorder: 'MediaRecorder' in window,
        },
      },
    });

    // Report to Google Analytics if available
    if ((window as any).gtag) {
      (window as any).gtag('event', 'exception', {
        description: `Video Error: ${error.message}`,
        fatal: false,
      });
    }
  }

  handleRetry = () => {
    // Enhanced logging for retry attempts
    console.group('🔄 VIDEO ERROR BOUNDARY - Retry Attempt');
    console.log('Previous Error:', this.state.error?.message);
    console.log('Error Name:', this.state.error?.name);
    console.log('Retry Timestamp:', new Date().toISOString());
    console.log('User Agent:', navigator.userAgent);
    console.groupEnd();

    Sentry.addBreadcrumb({
      message: 'User clicked retry on video error boundary',
      level: 'info',
      category: 'ui.click',
      data: {
        component: 'VideoErrorBoundary',
        previousError: this.state.error?.message,
      },
    });

    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
    if (this.props.onRetry) {
      this.props.onRetry();
    }
  };

  handleReportIssue = () => {
    // Enhanced logging for issue reporting
    console.group('📝 VIDEO ERROR BOUNDARY - Issue Report');
    console.log('Reporting Error:', this.state.error?.message);
    console.log('Error Name:', this.state.error?.name);
    console.log('Error Stack:', this.state.error?.stack);
    console.log('Component Stack:', this.state.errorInfo?.componentStack);
    console.log('Report Timestamp:', new Date().toISOString());
    console.log('User Agent:', navigator.userAgent);
    console.log('URL:', window.location.href);
    console.groupEnd();

    Sentry.addBreadcrumb({
      message: 'User clicked report issue on video error boundary',
      level: 'info',
      category: 'ui.click',
      data: {
        component: 'VideoErrorBoundary',
        error: this.state.error?.message,
      },
    });

    if (this.props.reportIssue) {
      this.props.reportIssue();
    }
  };

  render() {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '300px',
            padding: 3,
            textAlign: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.05)',
            borderRadius: '10px',
            border: '1px solid rgba(0, 0, 0, 0.1)',
          }}
        >
          <Stack spacing={2} alignItems="center" maxWidth="511px">
            <SvgIcon
              component={ErrorOutlineIcon}
              sx={{
                fontSize: '3rem',
                color: 'error.main',
              }}
            />

            <Typography variant="h4" color="error.main" gutterBottom>
              {this.props.fallbackTitle || 'Video Recording Error'}
            </Typography>

            <Typography variant="body1" color="text.secondary" mb={2}>
              {this.props.fallbackMessage ||
                'Something went wrong with the video component. This might be due to browser compatibility issues or camera/microphone access problems.'}
            </Typography>

            <Stack
              direction="row"
              spacing={2}
              flexWrap="wrap"
              justifyContent="center"
            >
              {this.props.showRefresh !== false && (
                <CustomButton
                  label="Try Again"
                  startIcon={<SvgIcon component={RefreshIcon} />}
                  onClick={this.handleRetry}
                  color="primary"
                />
              )}

              {this.props.reportIssue && (
                <CustomButton
                  label="Report Issue"
                  onClick={this.handleReportIssue}
                  color="secondary"
                />
              )}
            </Stack>

            {process.env.NODE_ENV === 'development' && (
              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  backgroundColor: 'grey.100',
                  borderRadius: 1,
                  maxWidth: '100%',
                  overflow: 'auto',
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  <strong>Debug Info:</strong>
                </Typography>
                <Typography
                  variant="caption"
                  component="pre"
                  sx={{
                    fontSize: '0.7rem',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {this.state.error?.message}
                  {this.state.errorInfo?.componentStack &&
                    `\n\nComponent Stack:${this.state.errorInfo.componentStack}`}
                </Typography>
              </Box>
            )}
          </Stack>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default VideoErrorBoundary;
